import { useCallback, useState } from "react";
import { applyJointLabelDragViaActuatorPositionControls } from "../../physics/jointLabelActuatorDrag";

const getPositionSliderRange = (joint) =>
  Array.isArray(joint?.range) && joint.range.length === 2 ? joint.range : [-Math.PI, Math.PI];

const getPositionSliderValue = (joint) =>
  Number.isFinite(Number(joint?.ref)) ? Number(joint.ref) : 0;

const getPositionSliderStep = (range) => (range[1] - range[0]) / 100 || 0.01;

export function useJointPositionSliderOverlayState({
  actuatorSliderState,
  isPhysicsRunning,
  safeJointMapObject,
  sceneActions,
  selectedJoint,
  showJointPositionSlider,
  showJoints,
}) {
  const [screenPosition, setScreenPosition] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const selectedJointDef =
    selectedJoint && safeJointMapObject ? safeJointMapObject[selectedJoint] : null;
  const visible =
    !!showJointPositionSlider &&
    !!showJoints &&
    !!screenPosition &&
    (!isPhysicsRunning || !!actuatorSliderState) &&
    !!selectedJointDef &&
    (selectedJointDef.kind === "hinge" || selectedJointDef.kind === "slide");
  const range = actuatorSliderState
    ? [actuatorSliderState.min, actuatorSliderState.max]
    : getPositionSliderRange(selectedJointDef);
  const value = actuatorSliderState
    ? actuatorSliderState.value
    : getPositionSliderValue(selectedJointDef);
  const step = actuatorSliderState ? actuatorSliderState.step : getPositionSliderStep(range);

  const stopEvent = useCallback((event) => {
    event.stopPropagation();
  }, []);

  const handlePointerDown = useCallback(
    (event) => {
      stopEvent(event);
      setIsDragging(true);
    },
    [stopEvent]
  );

  const handlePointerEnd = useCallback(
    (event) => {
      stopEvent(event);
      setIsDragging(false);
    },
    [stopEvent]
  );

  const handleValueChange = useCallback(
    (event) => {
      event.stopPropagation();
      const value = Number(event.target.value);
      if (
        isPhysicsRunning &&
        applyJointLabelDragViaActuatorPositionControls(selectedJoint, value)
      ) {
        return;
      }
      sceneActions.updateJointValue(selectedJoint, value);
    },
    [isPhysicsRunning, sceneActions, selectedJoint]
  );

  return {
    isDragging,
    onBlur: () => setIsDragging(false),
    onPointerCancel: handlePointerEnd,
    onPointerDown: handlePointerDown,
    onPointerMove: stopEvent,
    onPointerUp: handlePointerEnd,
    onStopEvent: stopEvent,
    onValueChange: handleValueChange,
    position: screenPosition,
    range,
    selectedJointDef,
    setScreenPosition,
    step,
    value,
    visible,
  };
}
